import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { ClientMessageType } from '@shared/types/messages';
import {
  SOUNDS,
  SOUND_CATEGORY_LABELS_ES,
  type SoundDef,
  type SoundBroadcastScope,
} from '@shared/data/sounds';

/**
 * Visor de sonidos in-game (dev/admin). Lista TODOS los sonidos del registro agrupados por `group`
 * (nivel básico) para escanearlos fácil. Por cada sonido: botón ▶ (reproducir local), y —sólo para
 * admins— botones para reproducirlo a otros: "Pant." (en pantalla), "Zona", "Mapa", "Todos", que
 * envían ADMIN_PLAY_SOUND (el server valida `isAdmin` y resuelve destinatarios por scope).
 *
 * Panel Phaser fijo (scrollFactor 0) con scroll por rueda (container enmascarado). Se togglea desde
 * un botón del menú. Ver docs/SOUND_AND_MUSIC.md.
 */
export class SoundBrowserPanel {
  private container: Phaser.GameObjects.Container | null = null;
  private inner: Phaser.GameObjects.Container | null = null;
  private mask: Phaser.Display.Masks.GeometryMask | null = null;
  private visible = false;
  private scrollY = 0;
  private contentHeight = 0;
  private viewHeight = 0;

  private static readonly WIDTH = 460;
  private static readonly ROW_H = 22;
  private static readonly DEPTH = 1200;

  constructor(private scene: GameScene) {}

  public isVisible(): boolean {
    return this.visible;
  }

  public toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  public show(): void {
    if (!this.container) this.build();
    this.visible = true;
    this.container?.setVisible(true);
  }

  public hide(): void {
    this.visible = false;
    this.container?.setVisible(false);
  }

  private build(): void {
    const { width, height } = this.scene.scale;
    const panelW = SoundBrowserPanel.WIDTH;
    const panelH = Math.min(520, height - 80);
    const x = Math.max(20, width - panelW - 20);
    const y = 60;

    this.container = this.scene.add
      .container(x, y)
      .setDepth(SoundBrowserPanel.DEPTH)
      .setScrollFactor(0);

    const bg = this.scene.add
      .rectangle(0, 0, panelW, panelH, 0x1a1a1a, 0.96)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x555555);
    const title = this.scene.addText(10, 8, 'Visor de sonidos', {
      fontSize: '15px',
      color: '#ffffff',
    });
    const close = this.scene.addText(panelW - 22, 6, '✕', {
      fontSize: '15px',
      color: '#ffffff',
      backgroundColor: '#553333',
      padding: { left: 6, right: 6, top: 2, bottom: 2 },
    }).setInteractive({ useHandCursor: true });
    close.on('pointerup', () => this.hide());

    const adminNote = this.scene.addText(
      10,
      28,
      this.scene.isLocalAdmin()
        ? 'Admin: ▶ local · Pant./Zona/Mapa/Todos → otros jugadores'
        : '▶ reproducir local (sólo admin puede enviar a otros)',
      { fontSize: '10px', color: '#aaaaaa', wordWrap: { width: panelW - 20 } }
    );

    this.container.add([bg, title, close, adminNote]);

    // Área scrolleable.
    const listTop = 46;
    this.viewHeight = panelH - listTop - 8;
    this.inner = this.scene.add.container(0, listTop).setScrollFactor(0);
    this.container.add(this.inner);

    // Máscara geométrica para clipear la lista.
    const maskShape = this.scene.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(x, y + listTop, panelW, this.viewHeight);
    this.mask = maskShape.createGeometryMask();
    this.inner.setMask(this.mask);

    this.populate();

    // Scroll por rueda cuando el puntero está sobre el panel.
    this.scene.input.on(
      'wheel',
      (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
        if (!this.visible) return;
        const px = this.scene.input.activePointer.x;
        const py = this.scene.input.activePointer.y;
        if (px < x || px > x + panelW || py < y || py > y + panelH) return;
        this.scrollBy(dy);
      }
    );
  }

  private scrollBy(dy: number): void {
    if (!this.inner) return;
    const maxScroll = Math.max(0, this.contentHeight - this.viewHeight);
    this.scrollY = Phaser.Math.Clamp(this.scrollY + dy * 0.5, 0, maxScroll);
    this.inner.setY(46 - this.scrollY);
  }

  private populate(): void {
    if (!this.inner) return;
    // Agrupar por `group`.
    const groups = new Map<string, SoundDef[]>();
    for (const def of Object.values(SOUNDS)) {
      if (!groups.has(def.group)) groups.set(def.group, []);
      groups.get(def.group)!.push(def);
    }

    const isAdmin = this.scene.isLocalAdmin();
    let rowY = 0;
    for (const [group, defs] of groups) {
      const header = this.scene.addText(8, rowY, `▾ ${group}`, {
        fontSize: '12px',
        color: '#ffd88a',
        fontStyle: 'bold',
      });
      this.inner.add(header);
      rowY += SoundBrowserPanel.ROW_H;

      for (const def of defs) {
        const label = this.scene.addText(16, rowY, def.label ?? def.id, {
          fontSize: '11px',
          color: '#dddddd',
        });
        this.inner.add(label);

        // Botón ▶ (local) siempre.
        const btnX = 220;
        this.inner.add(this.mkButton(btnX, rowY, '▶', () => this.scene.audio.play(def.id)));

        if (isAdmin) {
          const scopes: Array<{ label: string; scope: SoundBroadcastScope }> = [
            { label: 'Pant.', scope: 'screen' },
            { label: 'Zona', scope: 'zone' },
            { label: 'Mapa', scope: 'map' },
            { label: 'Todos', scope: 'all' },
          ];
          let bx = btnX + 26;
          for (const s of scopes) {
            const b = this.mkButton(bx, rowY, s.label, () => this.sendPlay(def.id, s.scope));
            this.inner.add(b);
            bx += 52;
          }
        }
        rowY += SoundBrowserPanel.ROW_H;
      }
      rowY += 6;
    }
    this.contentHeight = rowY;
  }

  private mkButton(x: number, y: number, label: string, onClick: () => void): Phaser.GameObjects.Text {
    const t = this.scene.addText(x, y, label, {
      fontSize: '10px',
      color: '#ffffff',
      backgroundColor: '#3a3a3a',
      padding: { left: 5, right: 5, top: 2, bottom: 2 },
    }).setInteractive({ useHandCursor: true });
    t.on('pointerover', () => t.setBackgroundColor('#555555'));
    t.on('pointerout', () => t.setBackgroundColor('#3a3a3a'));
    t.on('pointerup', () => onClick());
    return t;
  }

  private sendPlay(soundId: string, scope: SoundBroadcastScope): void {
    if (!this.scene.isLocalAdmin()) {
      // No-admin: sólo reproducir local (el server igual lo rechazaría).
      this.scene.audio.play(soundId);
      return;
    }
    this.scene.colyseusClient?.send({
      type: ClientMessageType.ADMIN_PLAY_SOUND,
      soundId,
      scope,
    });
  }
}
